import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { util, config } from '../_helpers';
import { toast } from "../_components/Toast";
import { accountService } from '../_services';
import { gapi } from 'gapi-script';
import { GoogleLogin } from 'react-google-login';

function Register({ history }) {
    const [registeredUser, setRegisteredUser] = useState(null);
    const [showSubscription, setShowSubscription] = useState(false);
    const [plans, setPlans] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [selectedSub, setSelectedSub] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [isSubmittingPlan, setIsSubmittingPlan] = useState(false);

    const initialValues = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        profilePic: '',
        acceptTerms: true
    };
    const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

    const initGoogleClient = () => {
        gapi.client.init({
            clientId: config.Google_Client_Id,
            scope: ['email']
        });
    }
    gapi.load('client:auth2', initGoogleClient);

    const redirectPostSuccess = (token) => {
        let redirectUrl = util.getParameterByName('redirect');
        window.location.href = redirectUrl + '?token=' + token;
    }

    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .required('First Name is required'),
        email: Yup.string()
            .email('Email is invalid')
            .required('Email is required'),
        phone: Yup.string()
            .matches(phoneRegExp, 'Phone is invalid')
            .required('Phone is required'),
        password: Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .required('Password is required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
            .required('Confirm Password is required'),
        acceptTerms: Yup.bool()
            .oneOf([true], 'Accept Terms & Conditions is required')
    });

    const formatDurationLabel = (durationCode) => {
        if (!durationCode) return '';
        switch(durationCode) {
            case 'monthly': return 'Monthly';
            case 'quarterly': return 'Quarterly';
            case 'half-yearly': return 'Half-Yearly';
            case 'yearly': return 'Yearly';
            default: return durationCode;
        }
    };

    const updateValidUntilDate = (frequency) => {
        if (!frequency) return;
        
        const date = new Date();
        
        switch(frequency) {
            case 'monthly':
                date.setDate(date.getDate() + 30);
                break;
            case 'quarterly':
                date.setDate(date.getDate() + 90);
                break;
            case 'half-yearly':
                date.setDate(date.getDate() + 180);
                break;
            case 'yearly':
                date.setDate(date.getDate() + 365);
                break;
            default:
                const durationMatch = frequency.match(/(\d+)\s*(\w+)/i);
                if (durationMatch) {
                    const value = parseInt(durationMatch[1]) || 1;
                    const unit = durationMatch[2].toLowerCase();
                    
                    if (unit.includes('month')) {
                        date.setDate(date.getDate() + (value * 30));
                    } else if (unit.includes('year')) {
                        date.setDate(date.getDate() + (value * 365));
                    } else if (unit.includes('day')) {
                        date.setDate(date.getDate() + value);
                    } else if (unit.includes('week')) {
                        date.setDate(date.getDate() + (value * 7));
                    }
                } else {
                    date.setDate(date.getDate() + 30);
                }
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setValidUntil(`${year}-${month}-${day}`);
    };

    const handlePostRegisterFlow = (userResponse) => {
        const userObj = userResponse.user;
        if (!userObj.planId && (userObj.isIAM === false || !userObj.isIAM)) {
            setRegisteredUser(userObj);
            const headers = { Authorization: `Bearer ${userObj.token}` };
            
            Promise.all([
                axios.get(`${config.AUTH_API_SERVER}/plan`, { headers }),
                axios.get(`${config.AUTH_API_SERVER}/subscriptions/subscriptions/`, { headers })
            ]).then(([planRes, subRes]) => {
                setPlans(planRes.data || []);
                setSubscriptions(subRes.data || []);
                setShowSubscription(true);
            }).catch(error => {
                console.error("Error fetching subscription plans:", error);
                redirectPostSuccess(userObj.token);
            });
        } else {
            redirectPostSuccess(userObj.token);
        }
    };

    const handlePlanSubmit = (e) => {
        e.preventDefault();
        if (!selectedSub || !registeredUser) return;
        
        setIsSubmittingPlan(true);
        
        axios.patch(
            `${config.AUTH_API_SERVER}/user/${registeredUser.id || registeredUser._id}`,
            { planId: selectedSub, validUntil: validUntil },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${registeredUser.token}`
                }
            }
        ).then(() => {
            toast.success("Subscription activated successfully!");
            redirectPostSuccess(registeredUser.token);
        }).catch(err => {
            console.error("Error saving plan:", err);
            toast.error("Failed to complete subscription activation. Please try again.");
            setIsSubmittingPlan(false);
        });
    };

    const onSubmit = (fields, { setStatus, setSubmitting }) => {
        setStatus && setStatus();
        accountService.register(fields)
            .then((res) => {
                handlePostRegisterFlow(res);
            })
            .catch(error => {
                setSubmitting(false);
                toast.error(error);
            });
    }

    const gLoginSuccess = (gData) => {
        if (gData.profileObj) {
            let newUser = initialValues;
            newUser.firstName = gData.profileObj.givenName;
            newUser.lastName = gData.profileObj.familyName;
            newUser.email = gData.profileObj.email;
            newUser.phone = "";
            newUser.password = gData.tokenId;
            newUser.confirmPassword = gData.tokenId;
            newUser.profilePic = gData.imageUrl;
            newUser.isEmailVerified = true;
            newUser.acceptTerms = true;
            newUser.thirdPartySignup = "GOOGLE";
            onSubmit(newUser, { setStatus: () => true, setSubmitting: () => true });
        }
    }

    const gLoginFailed = (err) => {
        console.log('google log in failed', err);
    }

    if (showSubscription) {
        return (
            <div>
                <h5 className="card-header">Select Subscription Plan</h5>
                <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
                    <h3 style={{ textAlign: 'center', color: '#1e293b', marginBottom: '8px', fontWeight: 'bold', fontSize: '20px' }}>
                        Choose Your Subscription Plan
                    </h3>
                    <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '24px', fontSize: '14px' }}>
                        Select a plan to complete your account setup and start using the platform.
                    </p>
                    
                    <div style={{ 
                        display: 'flex', 
                        gap: '20px', 
                        flexWrap: 'wrap', 
                        justifyContent: 'center',
                        marginBottom: '24px'
                    }}>
                        {subscriptions.map(sub => {
                            const associatedPlan = plans.find(p => p.id === sub.planId);
                            if (!associatedPlan) return null;
                            
                            const isSelected = selectedSub === sub._id;
                            
                            return (
                                <div 
                                    key={sub._id}
                                    onClick={() => {
                                        setSelectedSub(sub._id);
                                        updateValidUntilDate(sub.frequency);
                                    }}
                                    style={{
                                        flex: '1 1 200px',
                                        maxWidth: '260px',
                                        border: `2px solid ${isSelected ? '#3b82f6' : '#e2e8f0'}`,
                                        borderRadius: '12px',
                                        padding: '20px',
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#f8fafc' : 'white',
                                        boxShadow: isSelected ? '0 10px 15px -3px rgba(59, 130, 246, 0.1)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        position: 'relative'
                                    }}
                                >
                                    {isSelected && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '-12px',
                                            right: '20px',
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            padding: '2px 8px',
                                            borderRadius: '20px',
                                            textTransform: 'uppercase'
                                        }}>
                                            Selected
                                        </div>
                                    )}
                                    
                                    <div>
                                        <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', marginBottom: '8px' }}>
                                            {associatedPlan.planName}
                                        </h4>
                                        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: '12px' }}>
                                            <span style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>
                                                {sub.currency} {sub.frequencyPrice}
                                            </span>
                                            <span style={{ color: '#64748b', fontSize: '12px', marginLeft: '4px' }}>
                                                / {formatDurationLabel(sub.frequency)}
                                            </span>
                                        </div>
                                        
                                        <ul style={{ paddingLeft: '16px', margin: '0 0 16px 0', color: '#475569', fontSize: '12px', lineHeight: '1.5' }}>
                                            <li>Full access to platform features</li>
                                            <li>Includes {formatDurationLabel(sub.frequency)} support</li>
                                            <li>Cancel or change plans anytime</li>
                                        </ul>
                                    </div>

                                    <button
                                        type="button"
                                        style={{
                                            width: '100%',
                                            padding: '8px 0',
                                            borderRadius: '8px',
                                            border: 'none',
                                            fontWeight: '600',
                                            fontSize: '13px',
                                            backgroundColor: isSelected ? '#3b82f6' : '#f1f5f9',
                                            color: isSelected ? 'white' : '#1e293b',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        {isSelected ? 'Plan Selected' : 'Choose Plan'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    
                    {selectedSub && (
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '12px' }}>
                                Your subscription will be valid until: <strong style={{ color: '#1e293b' }}>{validUntil}</strong>
                            </p>
                            <button
                                onClick={handlePlanSubmit}
                                disabled={isSubmittingPlan}
                                style={{
                                    padding: '10px 24px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)'
                                }}
                            >
                                {isSubmittingPlan ? 'Activating Plan...' : 'Pay & Complete Sign Up'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ errors, touched, isSubmitting }) => (
                <Form>
                    <h5 className="card-header">Create New Account</h5>

                    <div className="d-flex" style={{ gap: "15px" }}>
                        <div className="form-group flex-fill">
                            <label>First Name</label>
                            <Field name="firstName" type="text" className={'form-control' + (errors.firstName && touched.firstName ? ' is-invalid' : '')} placeholder="First Name" />
                            <ErrorMessage name="firstName" component="div" className="invalid-feedback" />
                        </div>
                        <div className="form-group flex-fill">
                            <label>Last Name</label>
                            <Field name="lastName" type="text" className={'form-control' + (errors.lastName && touched.lastName ? ' is-invalid' : '')} placeholder="Last Name" />
                            <ErrorMessage name="lastName" component="div" className="invalid-feedback" />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Email Address</label>
                        <Field name="email" type="text" className={'form-control' + (errors.email && touched.email ? ' is-invalid' : '')} placeholder="Email" />
                        <ErrorMessage name="email" component="div" className="invalid-feedback" />
                    </div>

                    <div className="form-group">
                        <label>Phone Number</label>
                        <Field name="phone" type="text" className={'form-control' + (errors.phone && touched.phone ? ' is-invalid' : '')} placeholder="Phone" />
                        <ErrorMessage name="phone" component="div" className="invalid-feedback" />
                    </div>

                    <div className="d-flex" style={{ gap: "15px" }}>
                        <div className="form-group flex-fill">
                            <label>Password</label>
                            <Field name="password" type="password" className={'form-control' + (errors.password && touched.password ? ' is-invalid' : '')} placeholder="Password" />
                            <ErrorMessage name="password" component="div" className="invalid-feedback" />
                        </div>
                        <div className="form-group flex-fill">
                            <label>Confirm Password</label>
                            <Field name="confirmPassword" type="password" className={'form-control' + (errors.confirmPassword && touched.confirmPassword ? ' is-invalid' : '')} placeholder="Confirm" />
                            <ErrorMessage name="confirmPassword" component="div" className="invalid-feedback" />
                        </div>
                    </div>

                    <div className="form-group">
                        <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                            {isSubmitting && <span className="spinner-border spinner-border-sm mr-1"></span>}
                            Register Now
                        </button>
                    </div>

                    <div className="form-group d-flex justify-content-center align-items-center mt-3">
                        <Link to="login" className="btn btn-link p-0">
                            Already have an account? Sign In
                        </Link>
                    </div>

                    {config.Google_Client_Id && (
                        <div className="mt-4 pt-3 border-top border-secondary">
                            <div style={{ textAlign: "center" }} className="form-group">
                                <GoogleLogin
                                    clientId={config.Google_Client_Id}
                                    buttonText="SIGNUP WITH GOOGLE"
                                    onSuccess={gLoginSuccess}
                                    onFailure={gLoginFailed}
                                    cookiePolicy={'single_host_origin'}
                                    theme="dark"
                                />
                            </div>
                        </div>
                    )}

                    <div style={{ fontSize: "0.7em", color: "#666", textAlign: "center", marginTop: "20px" }}>
                        <span>&#169;</span>{new Date().getFullYear() + " " + config.Provider}. All Rights Reserved.
                    </div>
                </Form>
            )}
        </Formik>
    )
}

export { Register };